import { OnInit, Pipe, PipeTransform } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import * as selectors from '../../store/reducers/index';
import { auth } from '../../store/auth/auth.selectors';

// Returns whether items in provided org ID array are a subset of the user's selectable org IDs.
// To be safe, if an empty array is passed in, this returns false. (must be passed to async)
// Optional: Can pass in a roles array. The user then must have those roles for each organisation.
// Handles if the required roles passed in are of admin type.
@Pipe({
    name: 'involvedInOrgs',
    standalone: false
})
export class InvolvedInOrgsPipe implements PipeTransform {
  selectableRoles$: Observable<ReturnType<typeof selectors.org.getSelectableRoles>>;
  authRoles$: Observable<ReturnType<typeof auth.getRoles>>;
  constructor(private store: Store<any>) {
    // Organisation roles across organisations
    this.selectableRoles$ = this.store.select(selectors.org.getSelectableRoles);
    // Admin-type roles
    this.authRoles$ = this.store.select(auth.getRoles);
  }

  transform(organisationIds: string[], requiredRoles?: string[]): Observable<boolean> {
    return combineLatest([this.selectableRoles$, this.authRoles$]).pipe(
      filter(([selectableRoles, authRoles]) => !!selectableRoles && !!authRoles),
      map(([selectableRoles, authRoles]) => {
        // Early exit: No organisation IDs passed in.
        if (!organisationIds || !organisationIds.length) {
          return false;
        }

        // Early exit: Organisation IDs provided exist outside user's selectable ids.
        if (organisationIds.some((orgId) => !(selectableRoles ?? {})[orgId])) {
          return false;
        }

        if (!!requiredRoles?.length) {
          // Handling if the requested roles are of admin type.
          if (requiredRoles.some((requiredRole) => (authRoles ?? []).includes(requiredRole))) {
            return true;
          }

          // Does the user have a required role in every supplied organisation?
          if (requiredRoles && requiredRoles.length) {
            return organisationIds.every((orgId) => {
              return (selectableRoles[orgId] ?? []).some((orgRole) =>
                requiredRoles.includes(orgRole)
              );
            });
          }
        }

        return true;
      })
    );
  }
}
